import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Modules() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", category: "", sort_order: 0,
    is_addon: false, addon_price_monthly: 0, addon_price_yearly: 0,
  });

  const load = () => api.get("/platform/modules").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (m, is_active) => {
    try {
      await api.patch(`/platform/modules/${m.key}`, { is_active });
      toast.success(`${m.name} ${is_active ? "enabled" : "disabled"} platform-wide`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async () => {
    try {
      await api.patch(`/platform/modules/${editing.key}`, {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        addon_price_monthly: Number(form.addon_price_monthly) || 0,
        addon_price_yearly: Number(form.addon_price_yearly) || 0,
      });
      toast.success("Module updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const byCategory = rows.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="platform-modules-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Modules</h1>
        <p className="mt-1 text-sm text-slate-500">
          The feature catalog plans are built from. Disabling one here removes it from every tenant,
          whatever their plan says.
        </p>
      </div>

      {Object.entries(byCategory).map(([category, mods]) => (
        <div key={category} className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">{category}</div>
          <div className="divide-y divide-slate-100">
            {mods.map((m) => (
              <div
                key={m.key}
                className="flex items-center justify-between gap-4 px-4 py-3"
                data-testid={`module-row-${m.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{m.name}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                      {m.key}
                    </span>
                    {m.is_core && (
                      <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] uppercase text-blue-700">
                        Core
                      </span>
                    )}
                    {m.is_addon && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                        Paid add-on · {inr(m.addon_price_monthly)}/mo
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{m.description}</div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    In {m.plan_count} plan{m.plan_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button
                    onClick={() => {
                      setEditing(m);
                      setForm({
                        name: m.name, description: m.description,
                        category: m.category, sort_order: m.sort_order,
                        is_addon: !!m.is_addon,
                        addon_price_monthly: m.addon_price_monthly || 0,
                        addon_price_yearly: m.addon_price_yearly || 0,
                      });
                    }}
                    data-testid={`module-edit-${m.key}`}
                    className="text-xs font-medium text-violet-700 transition-colors duration-150 hover:text-violet-900"
                  >
                    Edit
                  </button>
                  <Switch
                    checked={m.is_active}
                    disabled={m.is_core}
                    data-testid={`module-toggle-${m.key}`}
                    onCheckedChange={(v) => toggle(m, v)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="module-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Display name</Label>
              <Input
                data-testid="module-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                data-testid="module-description-input"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <label className="flex items-start justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium">Sell as a paid add-on</span>
                  <span className="block text-[11px] text-slate-500">
                    Not bundled in the plan price. Plans list it under &ldquo;available add-ons&rdquo;
                    and each subscriber is billed separately.
                  </span>
                </span>
                <Switch data-testid="module-addon-switch" checked={!!form.is_addon}
                  disabled={editing?.is_core}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_addon: v }))} />
              </label>
              {form.is_addon && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Add-on price / month (₹)</Label>
                    <Input data-testid="module-addon-monthly" className="text-right font-mono"
                      value={form.addon_price_monthly}
                      onChange={(e) => setForm((f) => ({ ...f, addon_price_monthly: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Add-on price / year (₹)</Label>
                    <Input data-testid="module-addon-yearly" className="text-right font-mono"
                      value={form.addon_price_yearly}
                      onChange={(e) => setForm((f) => ({ ...f, addon_price_yearly: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input
                  data-testid="module-category-input"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Sort order</Label>
                <Input
                  data-testid="module-sort-input"
                  className="text-right font-mono"
                  value={form.sort_order}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="module-save" className="bg-violet-600 hover:bg-violet-700">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
